import { useTranslation } from "@hireroo/i18n";
import { ScoringPrecisionFeedbackForm } from "@hireroo/validator";
import { ErrorMessage } from "@hookform/error-message";
import { Typography } from "@mui/material";
import Stack from "@mui/material/Stack";
import * as React from "react";
import { useFormContext } from "react-hook-form";

import ResourceFieldV2 from "../../../../../modules/ResourceFieldV2/ResourceFieldV2";
import MultiChoiceField, { MultiChoiceFieldProps } from "../../../../../primitive/InputControl/MultiChoiceField/MultiChoiceField";
export type FeedbackFollowUpContentProps = {};

const FeedbackFollowUpContent: React.FC<FeedbackFollowUpContentProps> = _props => {
  const { t } = useTranslation();
  const methods = useFormContext<ScoringPrecisionFeedbackForm.ScoringPrecisionFeedbackFormSchema>();

  const entityTypes = methods.watch("entityTypes");
  const categoryReasons = methods.watch("categoryReasons");
  const hasChallenge = entityTypes?.includes("CHALLENGE");
  const hasSystemDesign = entityTypes?.includes("SYSTEM_DESIGN");

  const entityTypeOptions: MultiChoiceFieldProps["options"] = [
    {
      value: "CHALLENGE",
      displayName: t("コーディング形式"),
    },
    {
      value: "SYSTEM_DESIGN",
      displayName: t("システムデザイン形式"),
    },
  ];
  const categoryReasonOptions: MultiChoiceFieldProps["options"] = [
    {
      value: "UNCLEAR_CRITERIA",
      displayName: t("評価基準がわかりにくい"),
    },
    {
      value: "INSUFFICIENT_CRITERIA",
      displayName: t("評価基準が不足している"),
    },
    {
      value: "UNCLEAR_CHART",
      displayName: t("グラフがわかりにくい"),
    },
    {
      value: "SYSTEM_ERROR",
      displayName: t("システムの不具合が発生していた"),
    },
    {
      value: "INSUFFICIENT_CHEAT_DETECTION",
      displayName: t("不正が検知が出来ていない"),
    },
    {
      value: "OTHER",
      displayName: t("その他"),
    },
  ];

  const unclearCriteriaReasonOptions: MultiChoiceFieldProps["options"] = [
    ...(hasChallenge
      ? [
          {
            value: "CORRECTNESS",
            displayName: t("正解率"),
          },
          {
            value: "READABILITY",
            displayName: t("可読性"),
          },
          {
            value: "PERFORMANCE",
            displayName: t("パフォーマンス"),
          },
        ]
      : []),
    ...(hasSystemDesign
      ? [
          {
            value: "AVAILABILITY",
            displayName: t("可用性"),
          },
          {
            value: "SCALABILITY",
            displayName: t("スケーラビリティ"),
          },
        ]
      : []),
  ];

  return (
    <Stack spacing={3} width="100%" pb={3}>
      <ResourceFieldV2
        label={t("参考にならなかった問題形式を選択してください。（複数選択可）")}
        kind="REQUIRED"
        formLabel={{
          sx: {
            width: "100%",
            whiteSpace: "pre-wrap",
          },
        }}
      >
        <ErrorMessage
          name="entityTypes"
          errors={methods.formState.errors}
          render={({ message }) => (
            <Typography color="error" variant="caption">
              {message}
            </Typography>
          )}
        />
        <MultiChoiceField name="entityTypes" options={entityTypeOptions} />
      </ResourceFieldV2>
      <ResourceFieldV2
        label={t("参考にならなかった点があれば、以下のカテゴリから選択してください。（複数選択可）")}
        kind="REQUIRED"
        formLabel={{
          sx: {
            width: "100%",
            whiteSpace: "pre-wrap",
          },
        }}
      >
        <ErrorMessage
          name="categoryReasons"
          errors={methods.formState.errors}
          render={({ message }) => (
            <Typography color="error" variant="caption">
              {message}
            </Typography>
          )}
        />
        <MultiChoiceField name="categoryReasons" options={categoryReasonOptions} />
      </ResourceFieldV2>
      {categoryReasons?.includes("UNCLEAR_CRITERIA") && (hasChallenge || hasSystemDesign) && (
        <ResourceFieldV2
          label={t("どの評価基準がわかりにくいと感じましたか？（複数選択可）")}
          kind="REQUIRED"
          formLabel={{
            sx: {
              width: "100%",
              whiteSpace: "pre-wrap",
            },
          }}
        >
          <ErrorMessage
            name="unclearCriteriaReasons"
            errors={methods.formState.errors}
            render={({ message }) => (
              <Typography color="error" variant="caption">
                {message}
              </Typography>
            )}
          />
          <MultiChoiceField name="unclearCriteriaReasons" options={unclearCriteriaReasonOptions} />
        </ResourceFieldV2>
      )}
    </Stack>
  );
};

FeedbackFollowUpContent.displayName = "FeedbackFollowUpContent";

export default FeedbackFollowUpContent;
